import { Box, ListItemText } from "@mui/material";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";
import { ReactElement, useCallback } from "react";

import {
  GetNgpVanActivistCodeQuery,
  GetNgpVanActivistCodeQueryVariables,
  GetNgpVanActivistCodesQuery,
  GetNgpVanActivistCodesQueryVariables,
  NgpVanActivistCodeFieldsFragment,
  NgpVanDatabaseModeGqlType,
  NgpVanObjectType,
} from "~frontend/__generated__/graphql";
import {
  GET_NGPVAN_ACTIVIST_CODE,
  GET_NGPVAN_ACTIVIST_CODES,
} from "~frontend/graphql/integration";
import { palette } from "~frontend/theme";

import NgpVanObjectAutocomplete from "./NgpVanObjectAutocomplete";
import { BaseProps } from "./types";

const AUTO_REFRESH_ACTIVIST_CODES = 1000 * 60 * 60 * 24; // 1 day

export function NgpVanActivistCodeAutocomplete({
  onChange,
  ...baseProps
}: BaseProps & {
  onChange: (newValue: number | undefined) => void;
}): ReactElement {
  const { integrationId, databaseMode } = baseProps;

  return NgpVanObjectAutocomplete<
    NgpVanActivistCodeFieldsFragment,
    GetNgpVanActivistCodeQuery,
    GetNgpVanActivistCodeQueryVariables,
    GetNgpVanActivistCodesQuery,
    GetNgpVanActivistCodesQueryVariables
  >({
    ...baseProps,
    objectType: NgpVanObjectType.ActivistCodes,
    autoRefreshWindowMillis: AUTO_REFRESH_ACTIVIST_CODES,
    noResultsLabel: "No Activist Codes Found",
    errorLabel: "Error Loading Activist Codes",
    onChange: newActivistCode => {
      onChange(newActivistCode?.activistCodeId ?? undefined);
    },
    getLastRefreshTime: useCallback(
      integration => {
        const maybeTimestamp =
          databaseMode === NgpVanDatabaseModeGqlType.MyVoters
            ? integration.ngpVanMyVotersActivistCodeLastRefresh
            : integration.ngpVanMyCampaignActivistCodeLastRefresh;

        return maybeTimestamp ? new Date(maybeTimestamp) : null;
      },
      [databaseMode]
    ),
    getLastRefreshJob: useCallback(
      integration => {
        return (
          (databaseMode === NgpVanDatabaseModeGqlType.MyVoters
            ? integration.ngpVanMyVotersActivistCodeRefreshJob
            : integration.ngpVanMyCampaignActivistCodeRefreshJob) ?? null
        );
      },
      [databaseMode]
    ),

    getObjectQuery: GET_NGPVAN_ACTIVIST_CODE,
    getObjectQueryVariables: useCallback(
      value => ({
        input: {
          integrationId,
          databaseMode,
          activistCodeId: value,
        },
      }),
      [integrationId, databaseMode]
    ),
    getObjectQueryResultToObject: useCallback(
      data => data.getNgpVanActivistCode ?? null,
      []
    ),
    objectToGetObjectQueryResult: useCallback(
      activistCode => ({
        getNgpVanActivistCode: activistCode,
      }),
      []
    ),

    listObjectQuery: GET_NGPVAN_ACTIVIST_CODES,
    listObjectQueryVariables: useCallback(
      nameContains => ({
        input: {
          integrationId,
          databaseMode,
          nameContains: nameContains || null,
        },
      }),
      [integrationId, databaseMode]
    ),
    listObjectQueryResultToObjects: useCallback(
      data => data.getNgpVanActivistCodes,
      []
    ),

    searchTextFromObject: useCallback(activistCode => activistCode.name, []),
    valueFromObject: useCallback(
      activistCode => activistCode.activistCodeId,
      []
    ),
    listItemFromObject: useCallback(
      activistCode => (
        <ListItemText
          primary={activistCode.name}
          primaryTypographyProps={{ component: "div" }}
          secondary={
            activistCode.description ? (
              <Box sx={{ fontSize: "12px", color: palette.navy }}>
                <i>{activistCode.description}</i>
              </Box>
            ) : undefined
          }
          secondaryTypographyProps={{ component: "div" }}
        />
      ),
      []
    ),
  });
}

export function FormikNgpVanActivistCodeAutocomplete({
  label,
  name,
  integrationId,
  databaseMode,
  readOnly,
}: {
  label: string;
  name: string;
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  readOnly?: boolean;
}): ReactElement {
  const formik = useFormikContext<any>();

  return (
    <Field name={name}>
      {({
        field,
        meta,
      }: {
        field: FieldInputProps<unknown>;
        meta: FieldMetaProps<unknown>;
      }) => (
        <NgpVanActivistCodeAutocomplete
          label={label}
          integrationId={integrationId}
          databaseMode={databaseMode}
          readOnly={readOnly}
          value={field.value as number}
          onChange={async newValue => {
            // last argument is a boolean for whether to re-validate the form--
            // we only do that on the last call
            await formik.setFieldValue(name, newValue, false);
            await formik.setFieldTouched(name, true, true);
          }}
          error={Boolean(meta.touched && meta.error)}
          helperText={meta.touched ? meta.error : null}
        />
      )}
    </Field>
  );
}
